import { convertDate } from "@Services/luxonHelper";
import _ from "lodash";
import ExcelJS from "exceljs";
import type { UserDetails } from "@ServerTypes/accounts/accounts.schema";
import type { GetFormTemplateResponse } from "@ServerTypes/portal/portal.schema";
import type { FormTemplateField } from "@ServerTypes/forms/forms.schema";
import type { TransmittalFileMetadata, FormTemplateSection } from "@ServerTypes/transmittals/transmittals.schema";
import type { ProjectDataItem } from "@ServerTypes/projects/project.schema";
import type { ReviewApprovalStage } from "./ReviewsHelper";
import type { ReviewDataResult } from "@ServerTypes/reviews/reviews.schema";

/** One reviewer entry of `reviewDataResultSchema.approvalStages[].reviewers[]`. */
type ReviewApprovalStageReviewer = ReviewApprovalStage["reviewers"][number];

/**
 * SERVER GAP: `approvalStageReviewerSchema` (reviews.schema.ts) declares no `attachedToTransmittal`,
 * yet `getReviewData` returns it on the reviewer entry and this Fichtner export is driven entirely by
 * it. `/getReviewData` is a `handler:` route so its response is never validated/stripped at runtime,
 * which is why the field survives today. Declared here as an intersection rather than widening the
 * server schema (off-limits) — it must be added to `approvalStageReviewerSchema`.
 */
type ReviewerWithTransmittals = ReviewApprovalStageReviewer & {
  attachedToTransmittal?: { transmittalId: string; transmittalIndexAtProjectLevel?: number }[];
};

/** A forms-module answer object (`answerSchema`); `value` is `unknown` server-side. */
type FormFieldAnswer = NonNullable<NonNullable<FormTemplateField["questionFields"]>[number]["answer"]>;

/** A transmittal-module answer object (`formFieldAnswerSchema`). */
type TransmittalFieldAnswer = NonNullable<FormTemplateSection["questionFields"][number]["answer"]>;

/** The two project-level custom fields this transmittal sheet renders (TO / ATT). */
interface ProjectTransmittalCustomFields {
  TO?: FormFieldAnswer | "";
  ATT?: FormFieldAnswer | "";
}

interface ProjectCustomFieldResult {
  status: boolean;
  data: ProjectTransmittalCustomFields[];
}

/** One table row of the transmittal sheet. The two quoted keys are added after construction. */
interface TransmittalDrawingRow {
  name?: string;
  revision?: string;
  purpose?: string;
  "Drawing number"?: TransmittalFieldAnswer | "";
  "Doc Date"?: string;
}

export const processReviewDataForAttachedFiles = (result: ReviewDataResult | undefined, userDetails: UserDetails) => {
  // KNOWN BUG, preserved verbatim: `currentReviewLevel`, `approvalStages` and the `.find` result are
  // all optional/nullable on reviewDataResultSchema but are dereferenced without guards, so a Draft
  // review (no currentReviewLevel) or a caller who is not a reviewer at the current level throws here
  // and the Excel export dies with no toast. Does the server read any of it? No — read path only.
  // Fix: return early with a toast; guards would change the emit, so they are left alone.
  const { serial } = result!.currentReviewLevel!;
  const approvalStage = result!.approvalStages![serial - 1]!;
  const reviewer = approvalStage.reviewers.find((reviewer) => reviewer.userId === userDetails._id) as
    | ReviewerWithTransmittals
    | undefined;
  const attachedToTransmittal = reviewer!.attachedToTransmittal;

  return {
    // KNOWN BUG, preserved verbatim: the only caller (FilesInReview
    // `downloadTransmittalDetailsInExcels`) reads `.transmittalId` off this without a guard, so a
    // reviewer with an empty `attachedToTransmittal` throws there. Asserted non-null here to state
    // that assumption rather than moving the guard.
    reviewer,
    latestAttachedToTransmittal: attachedToTransmittal?.at(-1)!,
  };
};

export const processProjectCustomFieldData = (result: NonNullable<GetFormTemplateResponse["data"]>) => {
  const resultData: ProjectCustomFieldResult = { status: true, data: [] };
  const data: ProjectTransmittalCustomFields = {};

  if (result.template) {
    result.template?.forEach((template) => {
      if (template.questionFields) {
        template.questionFields.forEach((questionField) => {
          if (questionField.questionValue === "TO") {
            data.TO = questionField.answer || "";
          }
          if (questionField.questionValue === "ATT") {
            data.ATT = questionField.answer || "";
          }
        });
      }
    });
    resultData.data.push(data);
  }
  return resultData;
};

export const processDrawingNumbersAndDocDate = (result: TransmittalFileMetadata[]) => {
  const resultData: TransmittalDrawingRow[] = [];
  result.forEach((item) => {
    const data: TransmittalDrawingRow = {
      name: item.fileName,
      revision: item.revision,
      purpose: item.remarks,
    };
    if (item.formTemplate) {
      // KNOWN BUG, preserved verbatim: `transmittalFileMetadataSchema.formTemplate` is declared as an
      // OBJECT `{ _id, template?: FormTemplateSection[] }`, but this code iterates it as an ARRAY of
      // sections (`.forEach(formTemplate => formTemplate.questionFields)`). One of the two is wrong:
      // if the wire shape matches the schema, `.forEach` is not a function and this Fichtner-only
      // export throws; if the wire shape is really a section array, the schema is wrong and would
      // strip it on any route that validates responses. I could not check stored data (staging
      // unreachable). Does the server read it? No — read path only. The receiver is cast at the HEAD
      // so the optional chain and the emitted code are unchanged; nothing here is "fixed".
      (item as { formTemplate?: FormTemplateSection[] }).formTemplate?.forEach((formTemplate) => {
        if (formTemplate.questionFields) {
          formTemplate.questionFields.forEach((questionField) => {
            if (questionField.questionValue === "Drawing number") {
              data["Drawing number"] = questionField.answer || "";
            }
            if (questionField.questionValue === "Doc Date") {
              data["Doc Date"] = questionField.answer
                ? convertDate(Number(questionField.answer.value), "NormalDate")
                : "";
            }
          });
        }
      });
    }
    resultData.push(data);
  });

  return resultData;
};

export const getBase64 = async (url: string): Promise<string> => {
  const data = await fetch(url);
  const blob = await data.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data as string);
    };
  });
};

interface DoExcelExportActivityArgs {
  projectData: ProjectDataItem;
  filesDetails: TransmittalDrawingRow[];
  indexAtProjectLevel?: number;
  subject?: string;
  projectCustomField?: ProjectTransmittalCustomFields[];
}

export const doExcelExportActivity = async ({
  projectData,
  filesDetails,
  indexAtProjectLevel,
  subject,
  projectCustomField,
}: DoExcelExportActivityArgs) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Transmittal Form");

  const logoLeft = projectData.projectImageUrl
    ? await getBase64(projectData.projectImageUrl)
    : "data:image/jpeg;base64,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";

  const logoRight =
    "data:image/jpeg;base64,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";

  if (logoLeft) {
    const leftImageId = workbook.addImage({
      base64: logoLeft,
      extension: "png",
    });
    worksheet.addImage(leftImageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 150, height: 50 },
    });
  }

  if (logoRight) {
    const rightImageId = workbook.addImage({
      base64: logoRight,
      extension: "png",
    });
    worksheet.addImage(rightImageId, {
      tl: { col: 4, row: 0 },
      ext: { width: 150, height: 50 },
    });
  }
  worksheet.addRow([]);
  worksheet.addRow([]);

  worksheet.mergeCells("A3:E3");
  worksheet.getCell("A3").value = "DOCUMENT TRANSMITTAL FORM";
  worksheet.getCell("A3").alignment = {
    horizontal: "center",
    vertical: "middle",
  };
  worksheet.getCell("A3").font = { bold: true, size: 16 };

  worksheet.addRow([]);

  worksheet.mergeCells("A5:E5");
  worksheet.getCell("A5").value = projectData.description;
  worksheet.getCell("A5").alignment = {
    horizontal: "center",
    vertical: "middle",
  };
  worksheet.getCell("A5").font = { bold: true, size: 14 };

  // `answerSchema.value` is `z.unknown()` server-side (its shape depends on the question's fieldType),
  // so `_.get` yields `unknown`. Asserted to ExcelJS's CellValue rather than to `string`: the assertion
  // is erased, so exactly the same value is written to the cell as before.
  const leftFields = [
    ["TO : ", _.get(projectCustomField, "[0].TO.value", "") as ExcelJS.CellValue],
    ["ATT : ", _.get(projectCustomField, "[0].ATT.value", "") as ExcelJS.CellValue],
    ["REF. NO : ", `${projectData.projectId}-T-${indexAtProjectLevel}`],
    ["SUBJECT : ", subject || ""],
  ];

  const rightFields = [
    ["FROM : ", "Fichtner Consulting Engineers (I) Pvt. Ltd"],
    [
      "DATE : ",
      new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    ],
  ];

  for (let i = 0; i < leftFields.length || i < rightFields.length; i++) {
    const left = leftFields[i] || ["", ""];
    const right = rightFields[i] || ["", ""];

    const leftCell = worksheet.getCell(`A${6 + i * 2}`);
    leftCell.value = left[0];
    leftCell.font = { bold: true };
    worksheet.getCell(`B${6 + i * 2}`).value = left[1];

    const rightCell = worksheet.getCell(`D${6 + i * 2}`);
    rightCell.value = right[0];
    rightCell.font = { bold: true };
    worksheet.getCell(`E${6 + i * 2}`).value = right[1];
  }

  const purposeStartRow = 6 + Math.max(leftFields.length, rightFields.length) * 2;

  worksheet.getCell(`A${purposeStartRow}`).value = "PURPOSE : Filled by Sender";
  worksheet.getCell(`A${purposeStartRow}`).font = { bold: true };
  worksheet.getCell(`A${purposeStartRow}`).alignment = {
    horizontal: "left",
    vertical: "middle",
  };

  const purposeOptionsRow1 = ["1 : for execution", "2 : for information", "3 : for review", "4 : for recall"];

  const purposeOptionsRow2 = ["5 : for approval", "6 : other"];

  purposeOptionsRow1.forEach((option, idx: number) => {
    const colLetter = String.fromCharCode(66 + idx);
    worksheet.getCell(`${colLetter}${purposeStartRow}`).value = option;
    worksheet.getCell(`${colLetter}${purposeStartRow}`).alignment = {
      horizontal: "left",
      vertical: "middle",
    };
  });

  purposeOptionsRow2.forEach((option, idx: number) => {
    const colLetter = String.fromCharCode(66 + idx);
    worksheet.getCell(`${colLetter}${purposeStartRow + 1}`).value = option;
    worksheet.getCell(`${colLetter}${purposeStartRow + 1}`).alignment = {
      horizontal: "left",
      vertical: "middle",
    };
  });

  worksheet.addRow([]);
  const tableHeaders = ["Document No", "Rev. No", "Doc. Date", "Title or Description", "Purpose"];
  const headerRow = worksheet.addRow(tableHeaders);

  headerRow.font = { bold: true };

  headerRow.eachCell((cell) => {
    cell.alignment = { horizontal: "center", vertical: "middle" };
  });

  const purposeHeaderCell = headerRow.getCell(5);
  purposeHeaderCell.alignment = {
    horizontal: "center",
    vertical: "middle",
    textRotation: 90,
  };

  const tableRows = filesDetails.map((file) => {
    return [
      file["Drawing number"] ? file["Drawing number"].value : "",
      file.revision || "",
      file["Doc Date"] || "",
      file.name || "",
      file.purpose || "",
    ];
  });

  tableRows.forEach((row) => worksheet.addRow(row));

  for (let i = 0; i < 7; i++) {
    worksheet.addRow([]);
  }

  const noticeRow = worksheet.addRow(["* Computer generated Transmittal. Signature not required"]);
  noticeRow.getCell(1).font = { bold: true };

  const totalCountRow = worksheet.addRow(["", "", "", "", filesDetails.length]);
  totalCountRow.getCell(5).font = { bold: true };
  totalCountRow.getCell(5).alignment = {
    horizontal: "center",
    vertical: "middle",
  };
  const totalRow = worksheet.addRow(["", "", "", "", "TOTAL"]);
  totalRow.getCell(5).font = { bold: true };
  totalRow.getCell(5).alignment = {
    horizontal: "center",
    vertical: "middle",
    textRotation: 90,
  };

  worksheet.addRow([]);
  worksheet.addRow(["PLEASE RETURN ONE COPY OF ABOVE WITH YOUR COMMENTS AND/OR APPROVAL"]);
  worksheet.addRow(["PLEASE RETURN ONE DUPLICATE COPY OF TRANSMITTAL WITH SIGNATURE"]);
  worksheet.addRow([]);
  const row = worksheet.addRow(["RECEIVED BY", "", "", "DATE"]);

  worksheet.getCell(`A${row.number}`).font = { bold: true };

  worksheet.getCell(`D${row.number}`).font = { bold: true };
  const dateCell = worksheet.getCell(`D${row.number}`);
  dateCell.font = { bold: true };
  dateCell.alignment = { horizontal: "right" };

  worksheet.columns = [{ width: 25 }, { width: 15 }, { width: 15 }, { width: 35 }, { width: 20 }];

  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "DocumentTransmittalForm.xlsx";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
